// Editor del anexo técnico por BLOQUES (F4b · D1; rediseño F4c). Sin estado de página: recibe el anexo y avisa cada
// cambio. Dos usos con la misma pieza:
//  - en la cotización: título libre + bloques que se agregan de la biblioteca (o en blanco), se ordenan, se editan
//    y se quitan; la numeración 01, 02, … sale del orden;
//  - en la Configuración (`modoBiblioteca`): sólo la lista de bloques reutilizables, sin título.
//
// Ordenar los bloques: se ARRASTRAN del asa (⠿) y, para quien no arrastra, el asa también responde a las flechas
// ↑ ↓ del teclado, y siguen los botones «Subir» y «Bajar». Los tres hacen lo mismo. Cada bloque se puede plegar
// para ver el anexo entero de un vistazo. Quitar un bloque pide confirmar.
import { useState } from 'react';
import { ChevronDown, ChevronRight, ChevronUp, GripVertical, Plus, Trash2 } from 'lucide-react';
import type { AnexoTecnico, BloqueAnexo, TipoDeSeccion } from '../../types/pricer-datec.types';
import {
  LIMITES_ANEXO,
  NOMBRE_DEL_TIPO,
  actualizarSeccion,
  agregarBloque,
  agregarSeccion,
  bloqueEnBlanco,
  copiarBloque,
  moverBloque,
  moverSeccion,
  numerarSecciones,
  problemasDelAnexo,
  quitarBloque,
  quitarSeccion,
  renombrarBloque,
  reordenarBloque,
  seccionEnBlanco,
  totalDeSecciones,
} from '../../lib/anexo-tecnico';
import { SeccionDeAnexoEditor } from './SeccionDeAnexoEditor';
import { aviso, error } from './estilos';
import { ContadorDeLimite } from './ContadorDeLimite';
import { EstilosDelEditor } from './EstilosDelEditor';

interface Props {
  anexo: AnexoTecnico;
  onChange: (a: AnexoTecnico) => void;
  soloLectura?: boolean;
  /** Los bloques de la biblioteca de la empresa, para «Agregar de la biblioteca». */
  biblioteca?: BloqueAnexo[];
  /** Configuración: lista de bloques reutilizables (sin título del anexo ni biblioteca). */
  modoBiblioteca?: boolean;
}

type Destino = { i: number; lado: 'antes' | 'despues' } | null;

export function AnexoTecnicoEditor({
  anexo,
  onChange,
  soloLectura,
  biblioteca = [],
  modoBiblioteca,
}: Props) {
  const bloqueado = !!soloLectura;
  const [elegido, setElegido] = useState('');
  const [aQuitar, setAQuitar] = useState<number | null>(null);
  const [tipoNuevo, setTipoNuevo] = useState<TipoDeSeccion>('PARRAFO');
  const [plegados, setPlegados] = useState<ReadonlySet<string>>(new Set());
  // Arrastre: lo arrastrable es el ASA (así los campos de adentro siguen siendo texto seleccionable); `desde` es el
  // bloque que se lleva y `destino`, el hueco sobre el que está.
  const [desde, setDesde] = useState<number | null>(null);
  const [destino, setDestino] = useState<Destino>(null);
  const numeros = numerarSecciones(anexo);
  const opciones = { biblioteca: !!modoBiblioteca };
  // En sólo lectura nada sale, aunque un evento llegue.
  const emitir = (a: AnexoTecnico) => {
    if (!bloqueado) onChange(a);
  };
  const problemas = problemasDelAnexo(anexo);
  const topeDeBloques = modoBiblioteca
    ? LIMITES_ANEXO.bloquesEnBiblioteca
    : LIMITES_ANEXO.bloquesPorAnexo;

  const agregarDeLaBiblioteca = () => {
    const b = biblioteca.find((x) => x.id === elegido);
    if (!b) return;
    emitir(agregarBloque(anexo, copiarBloque(b), opciones));
    setElegido('');
  };

  const alternarPlegado = (id: string) =>
    setPlegados((antes) => {
      const n = new Set(antes);
      if (n.has(id)) n.delete(id);
      else n.add(id);
      return n;
    });

  const terminarArrastre = () => {
    setDesde(null);
    setDestino(null);
  };

  const soltar = () => {
    if (desde !== null && destino) {
      // El hueco elegido, en la lista de ANTES de sacar el bloque; al sacarlo, lo de abajo sube un lugar.
      const hueco = destino.lado === 'antes' ? destino.i : destino.i + 1;
      emitir(reordenarBloque(anexo, desde, desde < hueco ? hueco - 1 : hueco));
    }
    terminarArrastre();
  };

  return (
    <div>
      <EstilosDelEditor />
      {!modoBiblioteca && (
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
            gap: 16,
            marginBottom: 16,
          }}
        >
          <div>
            <label className="pd-field-label" htmlFor="anexo-titulo">
              Título del anexo
            </label>
            <input
              id="anexo-titulo"
              className="pd-field-input"
              value={anexo.titulo}
              maxLength={LIMITES_ANEXO.titulo}
              disabled={bloqueado}
              placeholder="Ej: Anexo técnico — Servicios profesionales"
              onChange={(e) => emitir({ ...anexo, titulo: e.target.value })}
            />
            <ContadorDeLimite largo={anexo.titulo.length} max={LIMITES_ANEXO.titulo} />
          </div>
          <div>
            <label className="pd-field-label" htmlFor="anexo-subtitulo">
              Subtítulo (opcional)
            </label>
            <input
              id="anexo-subtitulo"
              className="pd-field-input"
              value={anexo.subtitulo}
              maxLength={LIMITES_ANEXO.subtitulo}
              disabled={bloqueado}
              onChange={(e) => emitir({ ...anexo, subtitulo: e.target.value })}
            />
            <ContadorDeLimite largo={anexo.subtitulo.length} max={LIMITES_ANEXO.subtitulo} />
          </div>
        </div>
      )}

      {anexo.bloques.length === 0 && (
        <p
          style={{ fontSize: 13, color: 'hsl(var(--muted-foreground))', margin: '0 0 8px' }}
          role="status"
        >
          {modoBiblioteca
            ? 'Todavía no hay bloques en la biblioteca.'
            : 'El anexo no tiene bloques: si queda así, no se imprime.'}
        </p>
      )}

      <div style={{ display: 'grid', gap: 16, gridTemplateColumns: 'minmax(0, 1fr)' }}>
        {anexo.bloques.map((b, i) => {
          const plegado = plegados.has(b.id);
          return (
            <section
              key={b.id}
              aria-label={`Bloque ${i + 1}: ${b.nombre}`}
              className="dd-bloque"
              data-arrastrando={desde === i ? 'true' : undefined}
              data-destino={destino?.i === i && desde !== i ? destino.lado : undefined}
              onDragOver={(e) => {
                if (desde === null) return;
                e.preventDefault();
                const caja = e.currentTarget.getBoundingClientRect();
                const lado = e.clientY < caja.top + caja.height / 2 ? 'antes' : 'despues';
                if (destino?.i !== i || destino.lado !== lado) setDestino({ i, lado });
              }}
              onDrop={(e) => {
                e.preventDefault();
                soltar();
              }}
            >
              <div className="dd-bloque-cab">
                {!bloqueado && (
                  <div
                    role="button"
                    tabIndex={0}
                    draggable
                    className="dd-iconbtn dd-grip"
                    aria-label={`Reordenar el bloque ${i + 1}: arrastrá, o usá las flechas arriba y abajo`}
                    title="Arrastrá para reordenar (o usá las flechas ↑ ↓ con el asa enfocada)"
                    onDragStart={(e) => {
                      setDesde(i);
                      e.dataTransfer.effectAllowed = 'move';
                      e.dataTransfer.setData('text/plain', String(i));
                      // Lo que se ve al arrastrar es el bloque entero, no sólo el asa.
                      const bloque = e.currentTarget.closest('section');
                      if (bloque) e.dataTransfer.setDragImage(bloque, 24, 24);
                    }}
                    onDragEnd={terminarArrastre}
                    onKeyDown={(e) => {
                      if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
                        e.preventDefault();
                        emitir(moverBloque(anexo, i, e.key === 'ArrowUp' ? -1 : 1));
                      }
                    }}
                  >
                    <GripVertical size={16} aria-hidden="true" />
                  </div>
                )}
                <span className="dd-bloque-num" aria-hidden="true">
                  {i + 1}
                </span>
                <input
                  className="pd-field-input"
                  style={{ flex: '1 1 160px', minWidth: 0, fontWeight: 600 }}
                  aria-label={`Nombre del bloque ${i + 1}`}
                  value={b.nombre}
                  maxLength={LIMITES_ANEXO.nombreDeBloque}
                  disabled={bloqueado}
                  placeholder="Nombre del bloque"
                  onChange={(e) => emitir(renombrarBloque(anexo, i, e.target.value))}
                />
                {plegado && (
                  <span
                    style={{
                      fontSize: 12,
                      color: 'hsl(var(--muted-foreground))',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {b.secciones.length} {b.secciones.length === 1 ? 'sección' : 'secciones'}
                  </span>
                )}
                <div style={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  {!bloqueado && (
                    <>
                      <button
                        type="button"
                        className="dd-iconbtn"
                        disabled={i === 0}
                        aria-label={`Subir el bloque ${i + 1}`}
                        title="Subir el bloque"
                        onClick={() => emitir(moverBloque(anexo, i, -1))}
                      >
                        <ChevronUp size={16} aria-hidden="true" />
                      </button>
                      <button
                        type="button"
                        className="dd-iconbtn"
                        disabled={i === anexo.bloques.length - 1}
                        aria-label={`Bajar el bloque ${i + 1}`}
                        title="Bajar el bloque"
                        onClick={() => emitir(moverBloque(anexo, i, 1))}
                      >
                        <ChevronDown size={16} aria-hidden="true" />
                      </button>
                      <button
                        type="button"
                        className="dd-iconbtn dd-peligro"
                        aria-label={`Quitar el bloque ${i + 1}`}
                        title="Quitar el bloque"
                        onClick={() => setAQuitar(i)}
                      >
                        <Trash2 size={15} aria-hidden="true" />
                      </button>
                    </>
                  )}
                  <button
                    type="button"
                    className="dd-iconbtn"
                    aria-expanded={!plegado}
                    aria-label={`${plegado ? 'Desplegar' : 'Plegar'} el bloque ${i + 1}`}
                    title={plegado ? 'Mostrar el contenido' : 'Ocultar el contenido'}
                    onClick={() => alternarPlegado(b.id)}
                  >
                    {plegado ? (
                      <ChevronRight size={16} aria-hidden="true" />
                    ) : (
                      <ChevronDown size={16} aria-hidden="true" />
                    )}
                  </button>
                </div>
              </div>

              {aQuitar === i && !bloqueado && (
                <div
                  role="alertdialog"
                  aria-label="Confirmar"
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    flexWrap: 'wrap',
                    fontSize: 13,
                    margin: '0 12px 10px',
                    padding: '8px 12px',
                    borderRadius: 8,
                    background: 'hsl(var(--bloqueo-suave))',
                    color: 'hsl(var(--bloqueo))',
                  }}
                >
                  ¿Quitar este bloque y sus secciones?
                  <button
                    type="button"
                    className="dd-btn dd-btn-chico dd-btn-peligro"
                    onClick={() => {
                      emitir(quitarBloque(anexo, i));
                      setAQuitar(null);
                    }}
                  >
                    Sí, quitar
                  </button>
                  <button
                    type="button"
                    className="dd-btn dd-btn-chico"
                    onClick={() => setAQuitar(null)}
                  >
                    No
                  </button>
                </div>
              )}

              {!plegado && (
                <div style={{ padding: '0 12px 12px' }}>
                  {b.secciones.map((s, k) => (
                    <SeccionDeAnexoEditor
                      key={s.id}
                      seccion={s}
                      numero={modoBiblioteca ? undefined : numeros[s.id]}
                      soloLectura={soloLectura}
                      onChange={(nueva) => emitir(actualizarSeccion(anexo, i, k, nueva))}
                      onSubir={k > 0 ? () => emitir(moverSeccion(anexo, i, k, -1)) : undefined}
                      onBajar={
                        k < b.secciones.length - 1
                          ? () => emitir(moverSeccion(anexo, i, k, 1))
                          : undefined
                      }
                      onQuitar={() => emitir(quitarSeccion(anexo, i, k))}
                    />
                  ))}

                  {!bloqueado && (
                    <div
                      style={{
                        display: 'flex',
                        gap: 8,
                        marginTop: 12,
                        alignItems: 'center',
                        flexWrap: 'wrap',
                      }}
                    >
                      <label
                        className="pd-field-label"
                        htmlFor={`tipo-nuevo-${b.id}`}
                        style={{ margin: 0 }}
                      >
                        Agregar sección
                      </label>
                      <select
                        id={`tipo-nuevo-${b.id}`}
                        className="pd-field-input"
                        style={{ width: 140 }}
                        value={tipoNuevo}
                        onChange={(e) => setTipoNuevo(e.target.value as TipoDeSeccion)}
                      >
                        {(Object.keys(NOMBRE_DEL_TIPO) as TipoDeSeccion[]).map((t) => (
                          <option key={t} value={t}>
                            {NOMBRE_DEL_TIPO[t]}
                          </option>
                        ))}
                      </select>
                      <button
                        type="button"
                        className="dd-btn dd-btn-chico dd-btn-agregar"
                        aria-label={`Agregar la sección al bloque ${i + 1}`}
                        onClick={() =>
                          emitir(agregarSeccion(anexo, i, seccionEnBlanco(tipoNuevo), opciones))
                        }
                      >
                        <Plus size={13} aria-hidden="true" />
                        {`Agregar al bloque ${i + 1}`}
                      </button>
                    </div>
                  )}
                </div>
              )}
            </section>
          );
        })}
      </div>

      {!bloqueado && (
        <div
          style={{
            display: 'flex',
            gap: 8,
            marginTop: 16,
            flexWrap: 'wrap',
            alignItems: 'center',
            padding: 14,
            borderRadius: 12,
            background: 'hsl(var(--muted) / 0.5)',
            border: '1px dashed hsl(var(--muted-foreground) / 0.4)',
          }}
        >
          {!modoBiblioteca && biblioteca.length > 0 && (
            <>
              <label className="pd-field-label" htmlFor="anexo-biblioteca" style={{ margin: 0 }}>
                Bloque de la biblioteca
              </label>
              <select
                id="anexo-biblioteca"
                className="pd-field-input"
                style={{ width: 220 }}
                value={elegido}
                onChange={(e) => setElegido(e.target.value)}
              >
                <option value="">Elegí un bloque…</option>
                {biblioteca.map((b) => (
                  <option key={b.id} value={b.id}>
                    {b.nombre}
                  </option>
                ))}
              </select>
              <button
                type="button"
                className="dd-btn"
                disabled={!elegido || anexo.bloques.length >= topeDeBloques}
                aria-label={`Agregar el bloque de la biblioteca como bloque ${anexo.bloques.length + 1}, al final`}
                onClick={agregarDeLaBiblioteca}
              >
                Agregar de la biblioteca
              </button>
            </>
          )}
          <button
            type="button"
            className="dd-btn"
            disabled={anexo.bloques.length >= topeDeBloques}
            aria-label={`Agregar un bloque en blanco como bloque ${anexo.bloques.length + 1}, al final`}
            onClick={() => emitir(agregarBloque(anexo, bloqueEnBlanco('Bloque nuevo'), opciones))}
          >
            <Plus size={14} aria-hidden="true" />
            Agregar bloque en blanco
          </button>
          <span style={{ fontSize: 12, color: 'hsl(var(--muted-foreground))' }}>
            Los bloques nuevos se suman al final.
          </span>
        </div>
      )}

      {!modoBiblioteca && (
        <div style={{ fontSize: 12, color: 'hsl(var(--muted-foreground))', marginTop: 10 }}>
          {totalDeSecciones(anexo)} de {LIMITES_ANEXO.seccionesPorAnexo} secciones.
        </div>
      )}
      {anexo.bloques.length >= topeDeBloques && (
        <div style={aviso}>Llegaste al máximo de {topeDeBloques} bloques.</div>
      )}
      {problemas.length > 0 && (
        <ul role="alert" style={{ ...error, paddingLeft: 18 }}>
          {problemas.map((p) => (
            <li key={p}>{p}</li>
          ))}
        </ul>
      )}
    </div>
  );
}
